import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  FiSearch,
  FiShoppingBag,
  FiGlobe,
  FiAlertTriangle,
  FiUser,
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import LYRA_LOGO_FALLBACK from "../assets/lyra-mark.png";
import { useHeader } from "../contexts/HeaderContext";
import { MobileSearchSheet } from "./MobileSearchSheet";
import { MobileMenuSheet } from "./MobileMenuSheet";
import { MessageBar } from "./MessageBar";
import { LanguageDropdown } from "./LanguageDropdown";
import { HeaderSearchBar } from "./HeaderSearchBar";
import { AdFormatTabs } from "./AdFormatTabs";
import { useAdFormat, useHasAdFormatChoice } from "../hooks/useAdFormat";
import { useHideOnScroll } from "../hooks/useHideOnScroll";

/** Tailwind's `lg`, the breakpoint above which the header stops hiding on scroll. */
const LG_MEDIA_QUERY = "(min-width: 1024px)";
import { Popover } from "./Popover";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { Guest } from "../api/contracts/category-ads";
import { useConfiguration } from "../contexts/ConfigurationProvider";
import { sumGuestTypesAcrossUnits } from "../utils/guestUnitTotals";
import { format, isSameDay } from "date-fns";
import { getDateFnsLocale } from "../utils/language";
import { getLogoNavigationTarget } from "../utils/logoNavigation";
import { toasty } from "./Toast";
import { useWBEAuth } from "../contexts/wbe-auth-context";
import { useBookingFlowStore } from "../booking-flow/bookingFlowStore";

interface HeaderProps {
  stickyFooter?: React.ReactNode;
  onSearch?: () => void;
  cartCount?: number;
  showMobileSearch?: boolean;
  setShowMobileSearch?: React.Dispatch<React.SetStateAction<boolean>>;
  hideMobileSearchRow?: boolean;
  /** Hide the date picker / search bar entirely (desktop bar, mobile row, mobile sheet). */
  hideSearch?: boolean;
  /** Hide the shopping-cart icon. */
  hideCart?: boolean;
  /** Take over what the cart icon does. The v2 landing passes one: the cart there IS the
   *  summary panel already on the page, so navigating to the page the guest is standing on
   *  is a press that visibly does nothing. */
  onCartClick?: () => void;
  mobileSearchMode?: "search" | "roomInfo" | "package";
  mobileSearchCategoryAdId?: string;
  /** Package mode: bound category-ad ids + arrival window + stay period,
   *  forwarded to the sheet so its calendar blocks out-of-window arrivals and
   *  out-of-period nights. */
  mobileSearchPackageAdId?: string;
  /** Section the sheet opens on — see MobileSearchSheet's `openOnStep`. */
  mobileSearchStep?: "dates" | "guests" | "category";
  mobileSearchPackageCategoryAdIds?: string[];
  mobileSearchPackageArrivalDateFrom?: string | null;
  mobileSearchPackageArrivalDateTo?: string | null;
  mobileSearchPackageStayPeriodFrom?: string | null;
  mobileSearchPackageStayPeriodTo?: string | null;
  mobileSearchIconOnly?: boolean;
  onOpenMobileSearch?: () => void;
  /** Category-specific guest caps for room-info mode (passed straight to the sheet for clamping). */
  mobileSearchGuestLimitsOverride?: {
    adult: { min: number; max: number };
    teenager: { min: number; max: number };
    child: { min: number; max: number };
    infant: { min: number; max: number };
  };
  mobileSearchMaxTotalGuestsOverride?: number;
  onLogoClick?: () => void;
}

// The header logo is served as a short-lived signed URL. A failed load is
// usually a transient blip, so re-request the same URL a couple of times before
// giving up and showing the Lyra fallback mark.
const MAX_LOGO_LOAD_RETRIES = 2;
const LOGO_RETRY_DELAY_MS = 400;

export const Header: React.FC<HeaderProps> = ({
  stickyFooter,
  onSearch,
  cartCount = 0,
  showMobileSearch = false,
  setShowMobileSearch,
  hideMobileSearchRow = false,
  hideSearch: hideSearchProp = false,
  hideCart = false,
  onCartClick,
  mobileSearchMode = "search",
  mobileSearchCategoryAdId,
  mobileSearchPackageAdId,
  mobileSearchStep,
  mobileSearchPackageCategoryAdIds,
  mobileSearchPackageArrivalDateFrom,
  mobileSearchPackageArrivalDateTo,
  mobileSearchPackageStayPeriodFrom,
  mobileSearchPackageStayPeriodTo,
  mobileSearchIconOnly = false,
  onOpenMobileSearch,
  mobileSearchGuestLimitsOverride,
  mobileSearchMaxTotalGuestsOverride,
  onLogoClick,
}) => {
  const {
    selectedLanguage,
    setSelectedLanguage,
    selectedStartDate,
    selectedEndDate,
    hasTodayArrivalDeeplink,
    guests,
    units,
    bookingAvailability,
    bookingAvailabilityFetching,
    setOpenCalendarRequest,
    setSelectedStartDate,
    setSelectedEndDate,
    setGuestCount,
    updateGuest,
    getGuestsByType,
    getGuestCount,
    getBedTypeForAge,
  } = useHeader();

  const navigate = useNavigate();
  const { t } = useTranslation();
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const [useFallbackLogo, setUseFallbackLogo] = useState(false);
  const [logoRetryCount, setLogoRetryCount] = useState(0);
  const logoRetryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { configuration: wbeConfig, identifier } = useConfiguration();
  const hasAdFormatChoice = useHasAdFormatChoice();
  // Frozen while a sheet is open: the header is behind a full-screen overlay
  // then, and letting it toggle would only animate on the way back out.
  const scrolledAway = useHideOnScroll(!showMobileSearch && !showMobileMenu);
  const hideSearch = hideSearchProp;
  const formatRowVisible = !hideSearch && hasAdFormatChoice && !scrolledAway;
  const { account, isAuthenticated, logout } = useWBEAuth();

  const logoUrl = wbeConfig?.logotype?.url;
  // When the URL changes (e.g. the periodic config refetch renews the signed
  // URL), drop any retry/fallback state and try the real logo again.
  useEffect(() => {
    if (logoRetryTimerRef.current) clearTimeout(logoRetryTimerRef.current);
    setUseFallbackLogo(false);
    setLogoRetryCount(0);
  }, [logoUrl]);

  // Clear a pending retry timer on unmount.
  useEffect(
    () => () => {
      if (logoRetryTimerRef.current) clearTimeout(logoRetryTimerRef.current);
    },
    [],
  );

  const handleLogoError = () => {
    if (useFallbackLogo) return;
    if (logoRetryCount >= MAX_LOGO_LOAD_RETRIES) {
      // The blip would have cleared by now — the URL is genuinely unreachable
      // (e.g. expired). Show the Lyra mark until the next config refetch hands
      // us a fresh URL.
      setUseFallbackLogo(true);
      return;
    }
    if (logoRetryTimerRef.current) clearTimeout(logoRetryTimerRef.current);
    // Bumping the count remounts the <img> (via its key) so the browser
    // re-requests the same signed URL. Signed URLs can't be cache-busted with a
    // query param without breaking the signature, so a remount is the way.
    logoRetryTimerRef.current = setTimeout(() => {
      setLogoRetryCount((count) => count + 1);
    }, LOGO_RETRY_DELAY_MS);
  };

  const hasNoPaymentMethods = wbeConfig?.enabledPaymentMethods?.length === 0;
  const showAccountMenu = Boolean(wbeConfig?.requireLogin || isAuthenticated);
  const userInitials = account
    ? `${account.firstName[0] ?? ""}${account.lastName[0] ?? ""}`.toUpperCase()
    : "";

  const handleLoginClick = () => {
    if (!identifier) return;
    navigate(`/${identifier}/login`);
  };

  const handleLogoutClick = async () => {
    try {
      await logout();
      if (identifier) navigate(`/${identifier}`);
    } catch {
      toasty({ title: t("auth.logout_failed"), variant: "error" });
    }
  };

  const adultCount = getGuestCount("adult");
  const teenagerCount = getGuestCount("teenager");
  const childCount = getGuestCount("child");
  const infantCount = getGuestCount("infant");

  // Totals across every unit — the header's own collapsed guest summary (the
  // mobile trigger row) describes the whole party, not just the active unit.
  // adultCount/teenagerCount/etc. above stay scoped to the active unit: they
  // also feed the guest-edit handlers below and the sheet's fallback props,
  // both of which are legitimately per-unit.
  const guestTypeTotals = useMemo(
    () => sumGuestTypesAcrossUnits(units),
    [units],
  );

  const childGuests: Guest[] = getGuestsByType("child").map((guest) => ({
    age: guest.age,
    bedCapacityType: guest.bedCapacityType,
    ageType: guest.ageType,
  }));

  const infantGuests: Guest[] = getGuestsByType("infant").map((guest) => ({
    age: guest.age,
    bedCapacityType: guest.bedCapacityType,
    ageType: guest.ageType,
  }));

  const guestLimits = {
    adult: {
      min: wbeConfig?.maxConfig?.minGuests?.adults ?? 0,
      max: wbeConfig?.maxConfig?.maxGuests.adults ?? 10,
    },
    teenager: {
      min: wbeConfig?.maxConfig?.minGuests?.teenagers ?? 0,
      max: wbeConfig?.maxConfig?.maxGuests.teenagers ?? 10,
    },
    child: {
      min: wbeConfig?.maxConfig?.minGuests?.children ?? 0,
      max: wbeConfig?.maxConfig?.maxGuests.children ?? 10,
    },
    infant: {
      min: wbeConfig?.maxConfig?.minGuests?.infants ?? 0,
      max: wbeConfig?.maxConfig?.maxGuests.infants ?? 10,
    },
  };

  const totalGuests = adultCount + teenagerCount + childCount + infantCount;
  const maxTotalGuests = wbeConfig?.maxConfig?.maxTotalGuests ?? 16;
  const effectiveGuestLimits = mobileSearchGuestLimitsOverride ?? guestLimits;
  const effectiveMaxTotalGuests =
    mobileSearchMaxTotalGuestsOverride ?? maxTotalGuests;

  const handleDateSelect = (date: Date) => {
    if (
      selectedStartDate &&
      isSameDay(date, selectedStartDate) &&
      !selectedEndDate
    ) {
      setSelectedStartDate(null);
      return;
    }

    if (!selectedStartDate || (selectedStartDate && selectedEndDate)) {
      setSelectedStartDate(date);
      setSelectedEndDate(null);
      // keep calendar open to pick end date
    } else {
      if (date < selectedStartDate) {
        setSelectedStartDate(date);
        setSelectedEndDate(selectedStartDate);
      } else {
        setSelectedEndDate(date);
        if (window.innerWidth >= 768) {
          // Drop any pending open-request; the desktop pill (when mounted)
          // opens the guest popover next via its own internal state.
          setOpenCalendarRequest(false);
        }
      }
    }
  };

  const handleRangeChange = (from: Date, to: Date | null) => {
    setSelectedStartDate(from);
    setSelectedEndDate(to);
  };

  const formatDateDisplay = (date: Date | null) => {
    if (!date) return t("header.select_date");
    return format(date, "d MMM", { locale: getDateFnsLocale() });
  };

  const { isActivity } = useAdFormat();

  const handleCartClick = () => {
    if (cartCount === 0) return;
    if (onCartClick) return onCartClick();
    // Back into the plan, which restores to whatever step the guest left — for a booked unit
    // that is the add-on step, where the summary beside it lists the whole cart and every
    // control the cart page has. The plan's own route, so this works from anywhere: a unit
    // with nothing chosen shows the grid there with the plan still listed beside it.
    //
    // Gated on the plan: a guest who never entered the wizard has none to restore and belongs
    // on the cart page. The plan is site-scoped and lives in sessionStorage, so this is the
    // same signal the flow itself restores from.
    if (useBookingFlowStore.getState().plan) {
      navigate(`/${identifier}/plan${window.location.search}`);
      return;
    }
    navigate(`/${identifier}/booking`);
  };

  const handleLogoClick = () => {
    onLogoClick?.();
    // The logo goes back to the start page without throwing the guest's search
    // away: the dates and guests ride along in the URL, the booking code stays
    // in its sessionStorage, and the landing page opens on the start view
    // instead of re-running the search.
    // Still a full page load so landing data is always refreshed.
    window.location.assign(
      getLogoNavigationTarget(
        identifier,
        wbeConfig?.logotypeLinksToWebsite,
        wbeConfig?.siteUrl,
        window.location.search,
      ),
    );
  };

  const formatGuestInfo = () => {
    if (!isActivity && units.length > 1) {
      return t("header.units_short", { count: units.length });
    }
    const parts = [];
    if (guestTypeTotals.adult > 0)
      parts.push(t("header.adult", { count: guestTypeTotals.adult }));
    if (guestTypeTotals.teenager > 0)
      parts.push(t("header.teenager", { count: guestTypeTotals.teenager }));
    if (guestTypeTotals.child > 0)
      parts.push(t("header.child", { count: guestTypeTotals.child }));
    if (guestTypeTotals.infant > 0)
      parts.push(t("header.infant", { count: guestTypeTotals.infant }));
    if (parts.length === 0)
      return isActivity ? t("header.select_participants") : t("header.select_guests");
    return parts.join(", ");
  };

  const formatMobileInfo = () => {
    if (selectedStartDate && !selectedEndDate && hasTodayArrivalDeeplink) {
      return t("header.select_departure_and_guests");
    }

    const parts = [];
    if (selectedStartDate && selectedEndDate) {
      const startLabel = formatDateDisplay(selectedStartDate);
      const endLabel = formatDateDisplay(selectedEndDate);
      // A single-day activity search shows as one date, not "6 sep - 6 sep".
      parts.push(
        startLabel === endLabel ? startLabel : `${startLabel} - ${endLabel}`,
      );
      // Only show guest info when dates are selected too (avoid showing just "1 Adult")
      if (guestTypeTotals.adult > 0 || guestTypeTotals.child > 0) {
        parts.push(formatGuestInfo());
      }
    }

    if (parts.length === 0) {
      return t("header.select_date_and_guests");
    }

    return parts.join(" • ");
  };

  // Child/Infant field handlers
  const handleChildAgeChange = (childIndex: number, age: number) => {
    const childGuests = getGuestsByType("child");
    const guestIndex = guests.findIndex(
      (guest) => guest === childGuests[childIndex],
    );
    if (guestIndex !== -1) {
      updateGuest(guestIndex, { age, bedCapacityType: getBedTypeForAge(age) });
    }
  };

  const handleInfantAgeChange = (infantIndex: number, age: number) => {
    const infantGuests = getGuestsByType("infant");
    const guestIndex = guests.findIndex(
      (guest) => guest === infantGuests[infantIndex],
    );
    if (guestIndex !== -1) {
      updateGuest(guestIndex, { age, bedCapacityType: getBedTypeForAge(age) });
    }
  };

  // Publish the live header height so sticky elements below (e.g. the cart)
  // can offset themselves to sit clear of the pinned header at any viewport.
  //
  // The message bar counts, because it pins above the header and takes up the
  // same room. `stickyFooter` does not: a page passes its own bar in there
  // (LandingV2 passes the unit banner), and that bar publishes its height
  // separately as `--wbe-unit-banner-height`. Measuring the whole wrapper
  // counted it twice — the sticky summary sat a banner's height too low — and
  // made the translate long enough to take the banner off screen with the
  // header, where develop leaves it pinned at the top.
  const headerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const publishHeight = () => {
      const height = `${el.offsetHeight}px`;
      document.documentElement.style.setProperty("--wbe-header-height", height);
      // How much of it is actually on screen. On a phone the header slides off the top on
      // scroll but keeps its height, so anything pinned under it
      // by height alone was left hanging a header's worth down an empty page. On desktop it
      // never leaves, so the two agree there.
      const desktop =
        typeof window.matchMedia === "function" &&
        window.matchMedia(LG_MEDIA_QUERY).matches;
      const away = scrolledAway && !desktop;
      document.documentElement.style.setProperty(
        "--wbe-header-offset",
        away ? "0px" : height,
      );
    };
    publishHeight();
    const observer = new ResizeObserver(publishHeight);
    // Border box: the lg breakpoint swaps (and animates) the padding, which a content-box observer never sees.
    observer.observe(el, { box: "border-box" });
    return () => observer.disconnect();
  }, [scrolledAway]);

  return (
    <>
      {/* The wrapper is what moves. Transforming the header alone left the
        wrapper's box pinned over the top of the viewport at full height, so
        everything that pins below it had nowhere to go when the header slid
        away.

        It travels exactly the measured block's height, so the bar and the
        header leave and whatever the page pinned under them — `stickyFooter`,
        the unit banner on LandingV2 — lands at the top and stays. */}
      <div
        data-testid="wbe-header-sticky"
        className={clsx(
          "sticky top-0 z-40 transition-transform duration-300 ease-out motion-reduce:transition-none",
          scrolledAway
            ? "-translate-y-[var(--wbe-header-height,7rem)] lg:translate-y-0"
            : "translate-y-0",
        )}
      >
        <div ref={headerRef} data-testid="wbe-header-block">
          <MessageBar />
          <header
            data-testid="wbe-header"
            className={clsx(
              "relative border-b border-border bg-surface-page py-3 shadow-[0px_2px_8px_0px_rgba(0,0,0,0.06)] lg:pb-6",
              "transition-[padding] duration-300 ease-out motion-reduce:transition-none",
              !hideSearch && hasAdFormatChoice && "pt-0",
              // The format row brings its own padding, so whenever it is showing the
              // header's own would only stack a second gap above the chips.
              formatRowVisible ? "lg:pt-0" : "lg:pt-6",
            )}
          >
            {hasNoPaymentMethods && (
              <div className="mb-3 flex items-center justify-center gap-2 rounded-xl bg-status-warning-soft px-4 py-2 text-status-warning">
                <FiAlertTriangle className="h-4 w-4" />
                <span className="text-sm font-medium">
                  {t("header.no_payment_methods_available")}
                </span>
              </div>
            )}
            <div className="container mx-auto px-4 lg:px-8">
              {/* Own row: inside the one below, the tabs make the middle column
            taller than the logo and cart, and items-center then centres those
            on the tabs+search block instead of on the search bar itself. */}
              {!hideSearch && hasAdFormatChoice && (
                <div
                  className={clsx(
                    "mx-auto flex max-w-[1024px] justify-center overflow-hidden py-3",
                    "transition-all duration-300 ease-out motion-reduce:transition-none",
                    // The padding is what the clip box needs: overflow-hidden is
                    // required to collapse the row, and without it the chips' hover
                    // shadow and scale are cut off at the button's own edge.
                    scrolledAway
                      ? "lg:max-h-0 lg:py-0 lg:opacity-0"
                      : "lg:max-h-20",
                  )}
                  aria-hidden={scrolledAway}
                >
                  <AdFormatTabs />
                </div>
              )}
              <div className="mx-auto flex max-w-[1024px] items-center justify-between">
                {/* Left content */}
                <div className="flex items-center gap-4 md:gap-6">
                  {/* Visit icon */}
                  <button
                    type="button"
                    data-testid="header-logo-button"
                    className="cursor-pointer"
                    onClick={handleLogoClick}
                  >
                    <img
                      key={`logo-${logoRetryCount}`}
                      src={
                        useFallbackLogo
                          ? LYRA_LOGO_FALLBACK
                          : (logoUrl ?? LYRA_LOGO_FALLBACK)
                      }
                      alt={
                        wbeConfig?.logotype?.description ?? "Site logotype icon"
                      }
                      className="h-10 w-14 object-contain"
                      onError={handleLogoError}
                    />
                  </button>

                  {/* Language selector - hidden on mobile */}
                  <div className="relative hidden lg:block">
                    <button
                      className="flex items-center gap-1 transition-opacity duration-200 hover:opacity-80"
                      onClick={() =>
                        setShowLanguageDropdown(!showLanguageDropdown)
                      }
                    >
                      <span className="text-xs font-medium text-ink/60">
                        {t(`language.${selectedLanguage}`)}
                      </span>
                    </button>

                    <LanguageDropdown
                      isOpen={showLanguageDropdown}
                      onClose={() => setShowLanguageDropdown(false)}
                      selectedLanguage={selectedLanguage}
                      onLanguageChange={setSelectedLanguage}
                    />
                  </div>
                </div>

                {/* Search Bar - Desktop */}
                {!hideSearch && (
                  <div className="hidden lg:block">
                    <HeaderSearchBar onSearch={onSearch} />
                  </div>
                )}

                {/* Mobile/tablet search button in header row (default mode) */}
                {!hideSearch && !hideMobileSearchRow && (
                  <div
                    className={clsx("mx-3 my-1 flex lg:hidden", "h-12 flex-1")}
                  >
                    <button
                      onClick={() => {
                        onOpenMobileSearch?.();
                        setShowMobileSearch?.(true);
                      }}
                      className={clsx(
                        "flex items-center justify-center gap-1.5 border border-border bg-surface-card shadow-[0px_2px_6px_0px_rgba(0,0,0,0.05)]",
                        "w-full rounded-full px-3 py-2",
                      )}
                    >
                      <FiSearch className="h-5 w-5" />
                      <span className="block max-w-full truncate whitespace-nowrap text-sm font-medium leading-[1.14]">
                        {mobileSearchIconOnly
                          ? t("header.new_search")
                          : formatMobileInfo()}
                      </span>
                    </button>
                  </div>
                )}

                {/* Right content */}
                <div className="flex items-center gap-4">
                  {showAccountMenu && (
                    <>
                      {!isAuthenticated && (
                        <button
                          type="button"
                          className="flex h-9 w-9 items-center justify-center rounded-full border border-border bg-surface-card text-ink transition-opacity duration-200 hover:opacity-80"
                          onClick={handleLoginClick}
                          aria-label={t("auth.account_menu")}
                        >
                          <FiUser className="h-5 w-5" />
                        </button>
                      )}

                      {isAuthenticated && account && (
                        <Popover
                          placement="bottom"
                          offset={10}
                          className="w-56 rounded-xl border-border p-2 shadow-lg shadow-black/10"
                          trigger={
                            <button
                              type="button"
                              className="flex h-9 w-9 items-center justify-center rounded-full border border-border bg-surface-card text-ink transition-opacity duration-200 hover:opacity-80"
                              aria-label={t("auth.account_menu")}
                            >
                              {userInitials ? (
                                <span className="text-xs font-semibold">
                                  {userInitials}
                                </span>
                              ) : (
                                <FiUser className="h-5 w-5" />
                              )}
                            </button>
                          }
                          content={(close) => (
                            <div>
                              <div className="border-b border-border px-3 py-2">
                                <p className="truncate text-sm font-semibold text-ink">
                                  {account.firstName} {account.lastName}
                                </p>
                                <p className="truncate text-xs text-ink-secondary">
                                  {account.email}
                                </p>
                              </div>
                              <button
                                type="button"
                                className="mt-1 w-full rounded-lg px-3 py-2 text-left text-sm font-medium text-ink hover:bg-surface-page"
                                onClick={() => {
                                  close();
                                  handleLogoutClick();
                                }}
                              >
                                {t("auth.logout")}
                              </button>
                            </div>
                          )}
                        />
                      )}
                    </>
                  )}

                  {/* Shopping cart */}
                  {!hideCart && (
                    <div
                      className={clsx(
                        "flex items-center gap-1",
                        "transition-opacity duration-200 hover:opacity-80",
                        "cursor-pointer",
                        "relative",
                        cartCount === 0 && "cursor-not-allowed opacity-50",
                      )}
                      onClick={handleCartClick}
                      data-testid="cart-button"
                    >
                      <FiShoppingBag className="h-6 w-6" />
                      {cartCount > 0 && (
                        <div className="absolute -right-1.5 -top-1.5 flex h-4 w-4 items-center justify-center rounded-badge bg-primary">
                          <span className="text-[10px] font-bold leading-none text-on-primary">
                            {cartCount}
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Mobile Menu Button */}
                  <button
                    className="flex items-center justify-center lg:hidden"
                    onClick={() => setShowMobileMenu(true)}
                    aria-label={t("language.language")}
                  >
                    <FiGlobe className="h-6 w-6" />
                  </button>
                </div>
              </div>
            </div>
          </header>
        </div>
        {stickyFooter}
      </div>

      {/* Both sheets sit outside the sticky wrapper: the wrapper takes a
          transform when it scrolls away, and a transformed ancestor becomes
          the containing block for `position: fixed` descendants — which would
          pin these overlays to the bar instead of the viewport. */}
      {/* Mobile Search Sheet */}
      <MobileSearchSheet
        isOpen={showMobileSearch}
        onClose={() => setShowMobileSearch?.(false)}
        selectedStartDate={selectedStartDate}
        selectedEndDate={selectedEndDate}
        adultCount={adultCount}
        teenagerCount={teenagerCount}
        childCount={childCount}
        infantCount={infantCount}
        childGuests={childGuests}
        infantGuests={infantGuests}
        onDateSelect={handleDateSelect}
        onRangeChange={handleRangeChange}
        onAdultsChange={(count) => setGuestCount("adult", count)}
        onTeenagersChange={(count) => setGuestCount("teenager", count)}
        onChildrenChange={(count) => setGuestCount("child", count)}
        onInfantsChange={(count) => setGuestCount("infant", count)}
        onChildAgeChange={handleChildAgeChange}
        onInfantAgeChange={handleInfantAgeChange}
        onSearch={onSearch}
        guestLimits={effectiveGuestLimits}
        totalGuests={totalGuests}
        maxTotalGuests={effectiveMaxTotalGuests}
        guestTypesConfig={wbeConfig?.guestTypesConfig}
        bookingAvailability={bookingAvailability}
        bookingAvailabilityFetching={bookingAvailabilityFetching}
        mode={mobileSearchCategoryAdId ? "roomInfo" : mobileSearchMode}
        categoryAdId={mobileSearchCategoryAdId}
        packageAdId={mobileSearchPackageAdId}
        openOnStep={mobileSearchStep}
        packageCategoryAdIds={mobileSearchPackageCategoryAdIds}
        packageArrivalDateFrom={mobileSearchPackageArrivalDateFrom}
        packageArrivalDateTo={mobileSearchPackageArrivalDateTo}
        packageStayPeriodFrom={mobileSearchPackageStayPeriodFrom}
        packageStayPeriodTo={mobileSearchPackageStayPeriodTo}
      />

      {/* Mobile Menu Sheet */}
      <MobileMenuSheet
        isOpen={showMobileMenu}
        onClose={() => setShowMobileMenu(false)}
        selectedLanguage={selectedLanguage}
        onLanguageChange={setSelectedLanguage}
      />
    </>
  );
};
